import { create } from "zustand";
import { User } from "../types";
import { authAPI, userAPI } from "../services";
import i18n from "i18n/config";

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  error: string | null;
  login: (
    email: string,
    password: string,
    turnstileToken: string
  ) => Promise<void>;
  register: (
    email: string,
    password: string,
    firstName?: string,
    lastName?: string,
    referralCode?: string,
    turnstileToken?: string
  ) => Promise<void>;
  googleAuth: (token: string) => Promise<void>;
  logout: () => void;
  loadUser: () => Promise<void>;
  setUser: (user: User) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  token: localStorage.getItem("token"),
  isLoading: false,
  error: null,

  setUser: (user) => set({ user }),
  loadUser: async () => {
    const token = localStorage.getItem("token");

    if (!token) return;

    try {
      const user = await userAPI.getCurrentUser();
      set({ user });
      const preferences = user.preferences;
      if (preferences?.language) {
        i18n.changeLanguage(preferences.language);
      }
      // TODO: Update theme and lastModel
      // if (preferences.theme) {
      // }
      // if (preferences.lastModel) {
      // set({ lastModel: preferences.lastModel });
      // }
    } catch (error) {
      console.error("Error loading user:", error);
      // If there's an error (like expired token), clear the auth state
      localStorage.removeItem("token");
      set({ user: null, token: null });
    }
  },

  login: async (email: string, password: string, turnstileToken: string) => {
    set({ isLoading: true, error: null });
    try {
      const { access_token, user } = await authAPI.login(
        email,
        password,
        turnstileToken
      );
      localStorage.setItem("token", access_token);
      set({ token: access_token, user, isLoading: false });
    } catch (error) {
      set({
        error: "Login failed. Please check your credentials.",
        isLoading: false,
      });
      throw error;
    }
  },

  register: async (
    email: string,
    password: string,
    firstName?: string,
    lastName?: string,
    referralCode?: string,
    turnstileToken?: string
  ) => {
    set({ isLoading: true, error: null });
    try {
      const { token, user } = await authAPI.register(
        email,
        password,
        firstName,
        lastName,
        referralCode,
        turnstileToken
      );
      localStorage.setItem("token", token);
      set({ token, user, isLoading: false });
    } catch (error) {
      set({
        error: "Registration failed. Please try again.",
        isLoading: false,
      });
      throw error;
    }
  },

  googleAuth: async (token: string) => {
    set({ isLoading: true, error: null });
    try {
      const { access_token: authToken } = await authAPI.googleAuth(token);
      localStorage.setItem("token", authToken);
      set({ token: authToken, isLoading: false });
      // Load user data after successful Google auth
      await useAuthStore.getState().loadUser();
    } catch (error) {
      set({
        error: "Google authentication failed. Please try again.",
        isLoading: false,
      });
      throw error;
    }
  },

  logout: () => {
    localStorage.removeItem("token");
    set({ user: null, token: null });
  },
}));
